import { ReactElement, useCallback, useMemo, useState } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { CreateCompanyFormDataType } from "../../../stores/types/companyTypes.tsx";
import { StepCompanyAdmin } from "./steps/StepCompanyAdmin.tsx";
import { StepCompanyAndBrandDetails } from "./steps/StepCompanyAndBrandDetails.tsx";
import { RevButton, RevDialog, RevStepper } from "@revmatics/catalyst";
import { useAppDispatch } from "../../../stores/hooks/hooks.ts";
import { createCompany } from "../../../stores/thunks/companyThunk.tsx";
import { isValidUrl } from "../../../helpers/functions/hasValidHelpers.ts";

const stepperData = [
  { step: 1, title: "Set Company Admin" },
  { step: 2, title: "Company and Brand Details" },
];

type Props = {
  isOpenDialog: boolean;
  setOpenDialog: (open: boolean) => void;
};

export const AddCompanyDialog = ({ isOpenDialog, setOpenDialog }: Props) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dispatch = useAppDispatch();

  const methods = useForm<CreateCompanyFormDataType>({
    mode: "onChange",
    defaultValues: {
      first_name: "",
      last_name: "",
      email: "",
      company_name: "",
      brand_name: "",
      brand_website: "",
      enable_quickstart: false,
    },
  });
  const {
    handleSubmit,
    formState: { isValid, isSubmitting: formIsSubmitting },
    trigger,
    reset,
  } = methods;

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return <StepCompanyAdmin />;
      case 2:
        return <StepCompanyAndBrandDetails />;
      default:
        return null;
    }
  };
  const handleBackStep = useCallback(() => {
    setCurrentStep((prev) => prev - 1);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setOpenDialog(false);
    setCurrentStep(1);
    setCompletedSteps([]);
    reset({
      first_name: "",
      last_name: "",
      email: "",
      company_name: "",
      brand_name: "",
      brand_website: "",
      enable_quickstart: false,
    });
    setIsSubmitting(false);
  }, [setOpenDialog, reset]);

  const handleNextStep = useCallback(async () => {
    let isValid = false;
    let fieldsToValidate: (keyof CreateCompanyFormDataType)[] = [];

    switch (currentStep) {
      case 1:
        fieldsToValidate = ["first_name", "last_name", "email"];
        break;
      case 2:
        fieldsToValidate = ["company_name", "brand_name", "brand_website"];
        break;
    }

    isValid = await trigger(fieldsToValidate);

    if (isValid) {
      setCompletedSteps((prev) => [...new Set([...prev, currentStep])]);
      setCurrentStep((prev) => prev + 1);
    }
  }, [currentStep, trigger]);

  const onSubmit: SubmitHandler<CreateCompanyFormDataType> = useCallback(
    async (data: CreateCompanyFormDataType) => {
      setIsSubmitting(true);
      dispatch(
        createCompany({
          body: {
            ...data,
            brand_website: isValidUrl(data.brand_website)
              ? data.brand_website
              : `https://${data.brand_website}`,
          },

          callback: () => handleCloseDialog(),
        }),
      ).finally(() => {
        setIsSubmitting(false);
      });
    },
    [dispatch, handleCloseDialog],
  );

  const dialogActions = useMemo(() => {
    const actions: ReactElement[] = [];

    if (currentStep > 1) {
      actions.push(
        <RevButton
          key="back"
          text="Back"
          variant="tertiary"
          handleClick={handleBackStep}
        />,
      );
    }

    actions.push(
      <RevButton
        key="cancel"
        text="Cancel"
        variant="secondary"
        handleClick={handleCloseDialog}
      />,
    );

    if (currentStep < stepperData.length) {
      actions.push(
        <RevButton
          key="next"
          text="Next"
          variant="primary"
          handleClick={handleNextStep}
          disabled={!isValid || formIsSubmitting}
        />,
      );
    } else if (currentStep === stepperData.length) {
      actions.push(
        <RevButton
          key="submit"
          text="Create Company"
          variant="primary"
          handleClick={handleSubmit(onSubmit)}
          isLoading={isSubmitting || formIsSubmitting}
          disabled={!isValid || isSubmitting || formIsSubmitting}
        />,
      );
    }
    return actions;
  }, [
    currentStep,
    isValid,
    formIsSubmitting,
    handleBackStep,
    handleNextStep,
    handleCloseDialog,
    isSubmitting,
    handleSubmit,
    onSubmit,
  ]);

  return (
    <RevDialog
      isOpen={isOpenDialog}
      setIsOpen={setOpenDialog}
      title="Create a Company"
      btnList={dialogActions}
      scrollableContent={false}
      heightClassName="lg:h-[700px]"
      className="!pt-0"
      size="m"
      mobileSize="xs"
    >
      <FormProvider {...methods}>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col h-full w-full"
        >
          <RevStepper
            stepperData={stepperData}
            step={currentStep}
            successSteps={completedSteps}
            stretch
          />
          <div className="pt-6 flex-grow">{renderStepContent()}</div>
        </form>
      </FormProvider>
    </RevDialog>
  );
};
